import React from 'react';
import PropTypes from 'prop-types';
import DataTable from '../common/DataTable';
import DateTimeDisplay from '../common/DateTimeDisplay';
import { getIntegrationManifest } from '../../../integrations';
import StatusPill from '../../../shared/components/pill/StatusPill';
import StatePill from './StatePill';
import { healthState, syncState } from './integrationStates';

const DASH = <span className="text-muted">—</span>;

function hasRole(integration, role) {
  return (integration?.roles || []).includes(role);
}

function formatSchedule(integration) {
  if (!integration.sync_enabled) return 'Off';
  if (integration.sync_interval === 'daily') return 'Daily';
  return integration.sync_interval || '—';
}

export default function IntegrationsEnabledTable({
  integrations,
  actionLoadingId,
  selectedIntegrationId,
  onSelectIntegration,
  onToggleSync,
  onManualSync,
}) {
  // Columns are decided by what the listed integrations actually do. A ticketing connector has
  // no assets and no sync, so sync columns would be a row of meaningless dashes; an IoT one has
  // no outbound ticket health. An organization can hold both, so each column appears when any
  // listed integration has the role, and rows without it render a dash.
  const anyIot = integrations.some((row) => hasRole(row, 'iot_backend'));
  const anyTicketing = integrations.some((row) => hasRole(row, 'upstream_ticketing'));

  const columns = [
    {
      key: 'display_name',
      label: 'Integration',
      sortable: true,
      render: (row) => {
        const manifest = getIntegrationManifest(row.type);
        return (
          <div className="d-flex align-items-center gap-3">
            <div
              className={`d-flex align-items-center justify-content-center rounded-3 ${manifest?.colorClass || 'bg-light'}`}
              style={{ width: 40, height: 40 }}
            >
              {manifest?.logo ? (
                <img src={manifest.logo} alt={row.display_name} style={{ width: 24, height: 24 }} />
              ) : (
                <i className="bi bi-puzzle text-secondary" />
              )}
            </div>
            <div>
              <div className="fw-semibold">{row.display_name}</div>
              <div className="text-muted small">{row.name}</div>
            </div>
          </div>
        );
      },
      filterValue: (row) => `${row.display_name} ${row.name}`,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => (
        <StatusPill tone={row.status === 'active' ? 'success' : 'neutral'}>
          {row.status === 'active' ? 'Active' : 'Disabled'}
        </StatusPill>
      ),
    },
    ...(anyIot ? [
      {
        key: 'asset_count',
        label: 'Assets',
        sortable: true,
        render: (row) => (hasRole(row, 'iot_backend') ? row.asset_count ?? 0 : DASH),
      },
      {
        key: 'sync_schedule',
        label: 'Sync schedule',
        sortable: false,
        render: (row) => (hasRole(row, 'iot_backend') ? formatSchedule(row) : DASH),
      },
      {
        key: 'last_sync_at',
        label: 'Last sync',
        sortable: true,
        render: (row) => (hasRole(row, 'iot_backend') ? (
          <div>
            <DateTimeDisplay isoString={row.last_sync_at} />
            {row.last_sync_status && (
              <div className="mt-1">
                <StatePill state={syncState(row.last_sync_status)} />
              </div>
            )}
          </div>
        ) : DASH),
      },
      {
        key: 'next_sync_at',
        label: 'Next sync',
        sortable: true,
        render: (row) => {
          if (!hasRole(row, 'iot_backend')) return DASH;
          return row.sync_enabled
            ? <DateTimeDisplay isoString={row.next_sync_at} fallback="Scheduled" />
            : DASH;
        },
      },
      {
        key: 'sync_enabled',
        label: 'Auto sync',
        sortable: true,
        render: (row) => (hasRole(row, 'iot_backend') ? (
          <div className="form-check form-switch m-0" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={Boolean(row.sync_enabled)}
              disabled={actionLoadingId === row.id}
              onChange={(e) => onToggleSync(row, e.target.checked, e)}
              aria-label={`Toggle daily sync for ${row.display_name}`}
            />
          </div>
        ) : DASH),
      },
    ] : []),
    ...(anyTicketing ? [
      {
        key: 'health_status',
        label: 'Health',
        sortable: true,
        render: (row) => (hasRole(row, 'upstream_ticketing')
          ? <StatePill state={healthState(row.health_status)} />
          : DASH),
      },
    ] : []),
  ];

  const actions = [
    {
      key: 'sync',
      label: 'Sync now',
      icon: 'bi-arrow-repeat',
      onClick: (row, event) => onManualSync(row.id, event),
      // Syncing assets means nothing for a ticketing connector, and the endpoint refuses it.
      disabled: (row) => actionLoadingId === row.id || !hasRole(row, 'iot_backend'),
    },
  ];

  return (
    <DataTable
      items={integrations}
      columns={columns}
      actions={actions}
      getRowKey={(row) => row.id}
      selectedRowKey={selectedIntegrationId}
      onRowClick={onSelectIntegration}
      tableClassName="table-hover"
      emptyMessage="No integrations enabled yet. Set one up from the available integrations below."
      hideFilter={integrations.length <= 3}
      filterPlaceholder="Search enabled integrations…"
    />
  );
}

IntegrationsEnabledTable.propTypes = {
  integrations: PropTypes.arrayOf(PropTypes.object).isRequired,
  actionLoadingId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedIntegrationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSelectIntegration: PropTypes.func.isRequired,
  onToggleSync: PropTypes.func.isRequired,
  onManualSync: PropTypes.func.isRequired,
};
